import { Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';

import { Store } from '@ngrx/store';

import { ApiService, ConfigService } from '@app-core/services';
import { OVERLAY_DATA, OverlayRef } from '@app-core/services/overlay';
import { RequiredValidator } from '@app-core/validations';
import { ISupportEmailRequest } from '@app-model/request';
import { ButtonComponent } from '@app-shared/components/button/button.component';
import { DropdownOptionComponent } from '@app-shared/components/dropdown/dropdown-option.component';
import { DropdownComponent } from '@app-shared/components/dropdown/dropdown.component';
import { IconComponent } from '@app-shared/components/icon/icon.component';
import { InputControlComponent } from '@app-shared/components/input-control/input-control.component';
import { untilDestroyed } from '@app-shared/utils';
import * as fromRoot from '@app-store/reducers';

interface IRequestForm {
  email: FormControl<string>;
  category: FormControl<string>;
  details: FormControl<string>;
  img: FormControl<string>;
}

@Component({
  templateUrl: './report-problem-popup.component.html',
  styleUrls: ['./report-problem-popup.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    IconComponent,
    ButtonComponent,
    InputControlComponent,
    DropdownComponent,
    DropdownOptionComponent,
  ],
})
export class ReportProblemPopupComponent {
  private data = inject<{
    img: string;
  }>(OVERLAY_DATA);
  private fb = inject(NonNullableFormBuilder);
  private store$ = inject<Store<fromRoot.State>>(Store);
  private apiService = inject(ApiService);
  private _ref = inject<OverlayRef<ReportProblemPopupComponent>>(OverlayRef);
  private _configService = inject(ConfigService);

  requestForm: FormGroup<IRequestForm>;
  img: string = '';
  submitted: boolean = false;

  private _userName: string = '';

  readonly #untilDestroyed = untilDestroyed();

  readonly categoryTypes: { name: string; code: string }[] = [
    { name: 'Website Issue', code: 'website' },
    { name: 'Invalid Products/No Products', code: 'products' },
    { name: 'other', code: 'other' },
  ];

  constructor() {
    this.requestForm = this.fb.group({
      email: this.fb.control('', [RequiredValidator]),
      category: this.fb.control('website', [RequiredValidator]),
      details: this.fb.control(''),
      img: this.fb.control(''),
    });

    this.img = this.data.img;
    this.requestForm.get('img').setValue(this.img);

    this.store$
      .select((store) => store.userState.user)
      .pipe(takeUntilDestroyed())
      .subscribe((user) => {
        this._userName = user?.userName ?? '';
        if (this._userName) {
          this.requestForm.get('email').setValue(this._userName, { emitEvent: false });
        }
      });
  }

  submit(): void {
    const request: ISupportEmailRequest = {
      application: this._configService.appIdentifier,
      emailId: this.requestForm.get('email').value,
      username: this._userName,
      issueCategory: this.requestForm.get('category').value,
      issueDescription: this.requestForm.get('details').value,
      screenshot: this.requestForm.get('img').value,
    };

    this.apiService
      .supportEmail(request)
      .pipe(this.#untilDestroyed())
      .subscribe(() => (this.submitted = true));
  }

  close(): void {
    this._ref.close();
  }
}
